import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import type { TaxTreatmentSection as Section } from '../types';

interface Props {
  section?: Section;
  /** Opens the person's employee page, where the pair is confirmed. */
  onViewPerson: (personId: string) => void;
}

/** ME7 — people who are BOTH an employee and an independent contractor of this
 *  company. A HIGH warning that does not block the run. It is confirmed only on
 *  the person's engagements (a dedicated, generation-specific confirmation), so
 *  this section deliberately has no acknowledge / resolve / override actions.
 *  "unavailable" means the check could not run — never shown as "none". */
export const TaxTreatmentSection: React.FC<Props> = ({ section, onViewPerson }) => {
  const { t } = useTranslation('payroll');
  if (!section) return null;
  // Round 2: an unavailable check shows its notice AND every finding that is
  // still known — a failed re-check never hides a warning already raised.
  const notice = section.status === 'unavailable' ? (
      <div role="status" className="rounded-lg border border-warning bg-warning-soft p-4 text-sm text-warning-strong">
        <div className="flex items-center gap-2 font-medium">
          <AlertTriangle className="h-4 w-4" aria-hidden />
          {t('controlCenter.taxTreatment.unavailableTitle', 'Could not check for employee + contractor pairs')}
        </div>
        <p className="mt-1">
          {t('controlCenter.taxTreatment.unavailableBody',
            'This is not the same as "none found". Refresh the checks; if it keeps failing, report it before locking.')}
        </p>
        {section.errors.length > 0 && (
          <ul className="mt-2 list-disc pl-5 text-xs">
            {section.errors.map((e) => <li key={e}>{e}</li>)}
          </ul>
        )}
      </div>
  ) : null;
  if (section.findings.length === 0) return notice;
  const findings = (
    <div role="alert" className="rounded-lg border border-danger bg-danger-soft p-4 text-sm text-danger-strong">
      <div className="flex flex-wrap items-center gap-2 font-medium">
        <ShieldAlert className="h-4 w-4" aria-hidden />
        {t('controlCenter.taxTreatment.title', 'Confirm tax treatment')}
        <Badge variant="destructive">{t('controlCenter.taxTreatment.high', 'High')}</Badge>
        <span className="text-xs font-normal">
          {t('controlCenter.taxTreatment.nonBlocking', 'Does not block this run')}
        </span>
      </div>
      <p className="mt-1">
        {t('controlCenter.taxTreatment.body',
          'These people are both an employee and an independent contractor of this company. Salary and contractor fees are calculated separately; confirm how each pair is taxed on the person\'s engagements.')}
      </p>
      <ul className="mt-3 space-y-2">
        {section.findings.map((f) => (
          <li key={`${f.employmentEngagementId}:${f.contractorEngagementId}:${f.generation}`}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2 text-foreground">
            <div>
              <span className="font-medium">{f.fullName || f.personId}</span>
              {f.overlapFrom && (
                <span className="ml-2 text-xs">
                  {t('controlCenter.taxTreatment.from', 'both from {{date}}', { date: f.overlapFrom })}
                </span>
              )}
              {f.provisional && (
                <Badge variant="outline" className="ml-2">
                  {t('controlCenter.taxTreatment.provisional', 'Not approved yet')}
                </Badge>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={() => onViewPerson(f.personId)}>
              {t('controlCenter.taxTreatment.open', 'Open engagements')}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
  return notice ? <div className="space-y-3">{notice}{findings}</div> : findings;
};
